import { PdfDocumentBuilder, type FieldItem, type EmpresaInfo, type DocumentConfig } from '@/lib/pdfDocumentEngine';
import { loadEmpresaYConfigParaPdf } from '@/lib/pdfBranding';
import {
  sanitizarTextoContrato,
  GRADOS_UNE_50131,
  type ClausulaCertificado,
} from './plantillasPorDefecto';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export interface CertificadoPdfArgs {
  empresaId: string;
  cliente: {
    nombre: string;
    nombre_fiscal?: string | null;
    nif_cif?: string | null;
    dni_nif?: string | null;
    direccion_fiscal?: string | null;
    direccion?: string | null;
    codigo_postal?: string | null;
    poblacion?: string | null;
    email?: string | null;
    telefono?: string | null;
  };
  certificado: {
    numero_formateado: string;
    tipo: 'seguridad' | 'pci';
    fecha_instalacion: string;
    descripcion_instalacion?: string | null;
    referencia_proyecto?: string | null;
    grado_intrusion?: number | null;
    sedeNombre?: string | null;
    sedeDireccion?: string | null;
    objeto: string;
    clausulas: ClausulaCertificado[];
    texto_legal?: string;
    observaciones?: string | null;
    firma_cliente?: string | null;
    firma_empresa?: string | null;
    nombre_firmante_cliente?: string | null;
  };
  sistemas: Array<{
    tipo: string;
    tipoLabel: string;
    sedeNombre?: string;
    sedeDireccion?: string | null;
    elementos?: Array<{
      nombre: string;
      tipo_equipo?: string | null;
      marca?: string | null;
      modelo?: string | null;
      mac_serie?: string | null;
      ubicacion?: string | null;
      zona?: string | null;
      anio_fabricacion?: string | null;
    }>;
  }>;
  esBorrador?: boolean;
  empresaOverride?: EmpresaInfo | null;
  docConfigOverride?: Partial<DocumentConfig>;
}


function fmtDate(s: string): string {
  try {
    return format(new Date(s), "dd 'de' MMMM 'de' yyyy", { locale: es });
  } catch {
    return s;
  }
}

export async function generarCertificadoPdf(args: CertificadoPdfArgs): Promise<Blob> {
  let empresa: EmpresaInfo | null;
  let docConfig: Partial<DocumentConfig> | undefined;
  if (args.empresaOverride !== undefined) {
    empresa = args.empresaOverride;
    docConfig = args.docConfigOverride;
  } else {
    const loaded = await loadEmpresaYConfigParaPdf(args.empresaId);
    empresa = loaded.empresa;
    docConfig = loaded.docConfig;
  }
  const builder = await PdfDocumentBuilder.create(empresa, docConfig);

  const titulo = args.certificado.tipo === 'seguridad'
    ? 'Certificado de instalación — Seguridad'
    : 'Certificado de instalación — Protección contra incendios';

  builder.addBrandedHeader({
    titulo,
    numero: args.certificado.numero_formateado,
    fechaEmision: `Emitido: ${new Date().toLocaleDateString('es-ES')}`,
  });

  if (args.esBorrador) {
    builder.addSection('DOCUMENTO EN BORRADOR — NO EMITIDO');
    builder.addQuoteBlock(
      'Este documento es un BORRADOR del certificado de instalación y NO tiene validez. ' +
      'Hasta que sea emitido y firmado por ambas partes, el certificado no acredita la entrega de la instalación.'
    );
  }

  // Cliente + Empresa instaladora
  const dirCliente = [
    args.cliente.direccion_fiscal || args.cliente.direccion,
    [args.cliente.codigo_postal, args.cliente.poblacion].filter(Boolean).join(' '),
  ].filter(Boolean).join(' · ');

  const clienteFields: FieldItem[] = [
    { label: 'Titular', value: args.cliente.nombre_fiscal || args.cliente.nombre },
    { label: 'NIF/CIF', value: args.cliente.nif_cif || args.cliente.dni_nif || '—' },
  ];
  if (dirCliente) clienteFields.push({ label: 'Domicilio', value: dirCliente });
  if (args.cliente.email) clienteFields.push({ label: 'Email', value: args.cliente.email });
  if (args.cliente.telefono) clienteFields.push({ label: 'Teléfono', value: args.cliente.telefono });

  const empresaFields: FieldItem[] = [
    { label: 'Razón social', value: empresa?.razon_social || empresa?.nombre || '—' },
    { label: 'NIF', value: empresa?.nif_cif || '—' },
  ];
  if (empresa?.numero_dgp) empresaFields.push({ label: 'Nº DGP', value: empresa.numero_dgp });
  if (empresa?.numero_reci) empresaFields.push({ label: 'Nº RECI', value: empresa.numero_reci });

  builder.addInfoCardRow(
    { title: 'Titular de la instalación', fields: clienteFields },
    { title: 'Empresa instaladora', fields: empresaFields },
  );

  // Datos de la instalación
  builder.addSection('Datos de la instalación');
  const datosFields: FieldItem[] = [
    { label: 'Fecha de instalación', value: fmtDate(args.certificado.fecha_instalacion) },
  ];
  if (args.certificado.referencia_proyecto) {
    datosFields.push({ label: 'Referencia proyecto', value: args.certificado.referencia_proyecto });
  }
  const sedeFields: FieldItem[] = [];
  if (args.certificado.sedeNombre) {
    sedeFields.push({ label: 'Sede', value: args.certificado.sedeNombre });
  }
  if (args.certificado.sedeDireccion) {
    sedeFields.push({ label: 'Dirección', value: args.certificado.sedeDireccion });
  }
  if (sedeFields.length === 0) sedeFields.push({ label: 'Sede', value: '—' });
  builder.addInfoCardRow(
    { title: 'Instalación', fields: datosFields },
    { title: 'Ubicación', fields: sedeFields },
  );

  if (args.certificado.descripcion_instalacion) {
    builder.addBlockSubtitle('Descripción');
    const parr = sanitizarTextoContrato(args.certificado.descripcion_instalacion)
      .split('\n').filter(p => p.trim().length > 0);
    for (const p of parr) builder.addText(p, 9.5);
  }

  // Objeto
  const objeto = sanitizarTextoContrato(args.certificado.objeto);
  if (objeto) {
    builder.addSection('Objeto del certificado');
    builder.addQuoteBlock(objeto);
  }

  // Sistemas
  builder.addSection('Sistemas incluidos');
  if (args.sistemas.length === 0) {
    builder.addText('No se ha definido alcance de sistemas.', 9.5);
  } else {
    const rows = args.sistemas.map(s => [
      s.tipoLabel,
      s.sedeNombre || '—',
      s.sedeDireccion || '—',
    ]);
    builder.addTable(['Sistema', 'Sede', 'Dirección'], rows);

    // Detalle de elementos por sistema
    for (const s of args.sistemas) {
      const elementos = s.elementos || [];
      builder.addSpacer(3);
      builder.addBlockSubtitle(`Elementos instalados — ${s.tipoLabel}`);
      if (elementos.length === 0) {
        builder.addText('Sin elementos registrados.', 9.5);
        continue;
      }

      const tipoLower = (s.tipo || '').toLowerCase();
      const esIntrusion = tipoLower.includes('intrus');
      const esExtintores = tipoLower.includes('extintor');

      const equipoDe = (e: typeof elementos[number]) => {
        const partes = [e.tipo_equipo, [e.marca, e.modelo].filter(Boolean).join(' ')]
          .map(p => (p || '').trim())
          .filter(Boolean);
        const base = partes.join(' · ');
        return base || e.nombre || '—';
      };

      if (esIntrusion) {
        const headers = ['Equipo', 'Nº serie', 'Zona', 'Ubicación'];
        const rowsEl = elementos.map(e => [
          equipoDe(e),
          e.mac_serie || '—',
          e.zona || '—',
          e.ubicacion || '—',
        ]);
        builder.addTable(headers, rowsEl);
      } else if (esExtintores) {
        const headers = ['Equipo', 'Nº serie', 'Ubicación', 'Año fabricación'];
        const rowsEl = elementos.map(e => [
          equipoDe(e),
          e.mac_serie || '—',
          e.ubicacion || '—',
          e.anio_fabricacion || '—',
        ]);
        builder.addTable(headers, rowsEl);
      } else {
        const headers = ['Equipo', 'Nº serie', 'Ubicación'];
        const rowsEl = elementos.map(e => [
          equipoDe(e),
          e.mac_serie || '—',
          e.ubicacion || '—',
        ]);
        builder.addTable(headers, rowsEl);
      }
    }
  }


  // Grado UNE-EN 50131
  if (args.certificado.grado_intrusion && [1, 2, 3, 4].includes(args.certificado.grado_intrusion)) {
    const g = GRADOS_UNE_50131[args.certificado.grado_intrusion as 1 | 2 | 3 | 4];
    builder.addSection('Grado de seguridad UNE-EN 50131-1');
    builder.addBlockSubtitle(g.titulo);
    builder.addQuoteBlock(g.descripcion);
  }

  // Cláusulas
  if (args.certificado.clausulas.length > 0) {
    builder.addSection('Declaraciones');
    for (const c of args.certificado.clausulas) {
      const titulo = sanitizarTextoContrato(c.titulo) || '—';
      const contenido = sanitizarTextoContrato(c.contenido);
      builder.addBlockSubtitle(titulo);
      if (contenido) {
        const parrafos = contenido.split('\n').filter(p => p.trim().length > 0);
        for (const p of parrafos) builder.addText(p, 9.5);
      }
      builder.addSpacer(2);
    }
  }

  // Observaciones
  const obs = sanitizarTextoContrato(args.certificado.observaciones || '');
  if (obs) {
    builder.addSection('Observaciones');
    builder.addQuoteBlock(obs);
  }

  // Texto legal
  const legal = sanitizarTextoContrato(args.certificado.texto_legal || '');
  if (legal) {
    builder.addSpacer(4);
    builder.addLegalText(legal);
  }

  // Advertencia legal específica para CCTV / videovigilancia
  const hayCctv = args.sistemas.some(s => (s.tipo || '').toLowerCase() === 'cctv');
  if (hayCctv) {
    builder.addSpacer(4);
    builder.addSection('Advertencia legal — Videovigilancia');
    const advertenciaCctv = sanitizarTextoContrato(
      'Estimado/a cliente:\n\n' +
      'De acuerdo con el Reglamento (UE) 2016/679, de Proteccion de Datos (RGPD) y la Ley Organica 3/2018, de Proteccion de Datos Personales y Garantia de los Derechos Digitales (LOPDGDD), la responsabilidad legal sobre la recogida y tratamiento de los datos personales generados por los sistemas de seguridad (alarmas y videovigilancia - CCTV) recae siempre sobre el titular de la instalacion, ya sea una comunidad de propietarios, una empresa, un comercio o un particular.\n\n' +
      'En este sentido, corresponde al titular de la instalacion:\n\n' +
      ' - Garantizar la colocacion de la carteleria informativa de videovigilancia en accesos y zonas controladas.\n\n' +
      ' - Asegurar que el tratamiento de imagenes se limite exclusivamente a la finalidad de seguridad.\n\n' +
      ' - Adoptar las medidas tecnicas y organizativas necesarias para proteger los datos personales.\n\n' +
      'La empresa instaladora/mantenedora no ostenta la condicion de responsable del tratamiento de datos, sino la de encargado del tratamiento, unicamente en los supuestos en los que se le otorgue acceso a los sistemas bajo las instrucciones del titular.\n\n' +
      'En resumen: La legalizacion y cumplimiento de la normativa de proteccion de datos es obligacion exclusiva del titular de la instalacion, que debe contactar con una empresa especializada en proteccion de datos para garantizar la adecuacion legal de su sistema de seguridad.\n\n' +
      'Atentamente,'
    );
    const parrafos = advertenciaCctv.split('\n').filter(p => p.trim().length > 0);
    for (const p of parrafos) {
      builder.addText(p, 9.5);
      builder.addSpacer(1);
    }
  }

  // Firmas
  builder.addSignatureBlock([
    { label: 'Empresa instaladora', imageData: args.certificado.firma_empresa || null, nombre: empresa?.razon_social || empresa?.nombre || '', esEmpresa: true },
    { label: 'Titular de la instalación', imageData: args.certificado.firma_cliente || null, nombre: args.certificado.nombre_firmante_cliente || args.cliente.nombre_fiscal || args.cliente.nombre },
  ]);

  const pdf = builder.finalize();

  if (args.esBorrador) {
    const pageCount = pdf.getNumberOfPages();
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      try {
        // @ts-ignore
        const GState = (pdf as any).GState;
        if (GState) {
          // @ts-ignore
          pdf.setGState(new GState({ opacity: 0.15 }));
        }
      } catch { /* noop */ }
      pdf.setTextColor(180, 0, 0);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(90);
      pdf.text('BORRADOR', pageW / 2, pageH / 2, {
        align: 'center',
        baseline: 'middle',
        angle: 35,
      });
      try {
        // @ts-ignore
        const GState = (pdf as any).GState;
        if (GState) {
          // @ts-ignore
          pdf.setGState(new GState({ opacity: 1 }));
        }
      } catch { /* noop */ }
      pdf.setTextColor(0, 0, 0);
    }
  }

  return pdf.output('blob');
}
